import Footer from "@/comps/footer"
import Header from "@/comps/header"
import { propsWithTranslation } from "@/ssr/common"
import { devices } from "@/utils/breakpoints"
import { useTranslation } from "next-i18next"
import styled from "styled-components"

import Cookies from "@/comps/cookies"
import Favicon from "@/comps/favicon"

import {
  AdjustProvider,
  UsePageViewEvent,
} from "@/utils/adjust"

import { GlobalStyle } from "./careers"
import { AppProviderProps, queryClient } from "./_app"

import { ClientSideAuthProvider } from "@/utils/client-auth"
import { GoogleOneTap } from "@/utils/googleapi"
import { NotificationProvider } from "@/utils/notification"

import { CoinsProvider } from "@/utils/coins"
import { ThemeProvider } from "next-themes"
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3"
import { QueryClientProvider } from "react-query"
import { EditorParamsProviders } from "../sections/editor/editor"

const Container = styled.div`
  overflow: hidden;
  width: var(--w-screen);
  padding: 25px 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 14px;

  min-height: 100vh;
`

const Icon = () => (
  <svg
    width="1728"
    height="181"
    viewBox="0 0 1728 181"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0 93.771H377.559C399.567 93.771 417.408 75.9299 417.408 53.9216V18.0988C417.408 9.2077 424.616 2 433.507 2V2C442.398 2 449.606 9.2077 449.606 18.0988V159.721C449.606 170.369 458.238 179 468.885 179V179C479.532 179 488.164 170.369 488.164 159.721V133.62C488.164 111.612 506.005 93.771 528.013 93.771H697.202"
      stroke="#E0E2EB"
      strokeWidth="4"
    />
    <path
      d="M1728 93.771H1339.66C1317.65 93.771 1299.81 75.9299 1299.81 53.9216V18.5146C1299.81 9.39385 1292.42 2 1283.3 2V2C1274.18 2 1266.78 9.39383 1266.78 18.5146V159.223C1266.78 170.146 1257.93 179 1247.01 179V179C1236.08 179 1227.23 170.146 1227.23 159.223V133.62C1227.23 111.612 1209.39 93.771 1187.38 93.771H1010"
      stroke="#E0E2EB"
      strokeWidth="4"
    />
    <path
      d="M753.271 74.7922V116.758C753.271 122.517 750.938 127.585 747.522 130.508C744.194 133.357 739.778 134.256 735.012 131.726C722.485 125.076 715.568 109.608 715.353 94.2046C715.137 78.7873 721.618 64.4212 734.693 59.5107C740.223 57.4337 744.757 58.4099 747.925 61.1084C751.163 63.8666 753.271 68.6835 753.271 74.7922Z"
      fill="#F5F6FC"
      stroke="#E0E2EB"
      strokeWidth="4"
    />
    <path
      d="M699.201 100.927V89.7885C699.201 76.2494 708.795 64.6088 722.085 62.0234L738.646 58.8018V132.75L720.803 128.411C708.125 125.328 699.201 113.974 699.201 100.927Z"
      fill="white"
      stroke="#E0E2EB"
      strokeWidth="4"
    />
    <path
      d="M745.562 74.7148H772.668"
      stroke="#E0E2EB"
      strokeWidth="4"
      strokeLinecap="round"
    />
    <path
      d="M745.562 116.955H772.668"
      stroke="#E0E2EB"
      strokeWidth="4"
      strokeLinecap="round"
    />
    <path
      d="M953.931 74.7922V116.758C953.931 122.517 956.263 127.585 959.679 130.508C963.007 133.357 967.423 134.256 972.189 131.726C984.717 125.076 991.633 109.608 991.848 94.2046C992.064 78.7873 985.583 64.4212 972.508 59.5107C966.978 57.4337 962.444 58.4099 959.276 61.1084C956.038 63.8666 953.931 68.6835 953.931 74.7922Z"
      fill="#F5F6FC"
      stroke="#E0E2EB"
      strokeWidth="4"
    />
    <path
      d="M1008 100.927V89.7885C1008 76.2494 998.406 64.6088 985.116 62.0234L968.556 58.8018V132.75L986.398 128.411C999.076 125.328 1008 113.974 1008 100.927Z"
      fill="white"
      stroke="#E0E2EB"
      strokeWidth="4"
    />
    <ellipse
      rx="3.18888"
      ry="3.18795"
      transform="matrix(-1 0 0 1 960.985 76.8828)"
      fill="#E0E2EB"
    />
    <ellipse
      rx="3.18888"
      ry="3.18795"
      transform="matrix(-1 0 0 1 960.985 118.326)"
      fill="#E0E2EB"
    />
  </svg>
)

const Title = styled.h1`
  color: var(--color-primary-500);
  text-align: center;
  max-width: 278px;

  font-weight: 700;
  font-size: 22px;
  line-height: 28px;

  @media ${devices.tablet} {
    font-size: 36px;
    line-height: 44px;
    max-width: 400px;
  }

  @media ${devices.desktop} {
    font-size: 52px;
    line-height: 52px;
    max-width: unset;
  }
`

const Subtitle = styled.h2`
  color: var(--color-blue-600);
  text-align: center;
  max-width: 278px;

  font-weight: 400;
  font-size: 16px;
  line-height: 22px;

  @media ${devices.tablet} {
    font-size: 24px;
    line-height: 42px;
    max-width: unset;
  }

  @media ${devices.desktop} {
    font-size: 28px;
    line-height: 42px;
  }
`

const Background = styled.div`
  width: 130vw;
  @media ${devices.tablet} {
    width: var(--w-screen);
  }

  display: flex;
  flex-direction: column;
  align-items: center;

  svg {
    path[stroke="#E0E2EB"] {
      stroke: var(--color-blue-100);
    }

    ellipse[fill="#E0E2EB"] {
      fill: var(--color-blue-100);
    }

    path[fill="#F5F6FC"] {
      fill: var(--color-background);
    }

    .dark & path[fill="white"] {
      fill: #37383f;
    }
  }
`
const ServerError = () => {
  const { t } = useTranslation()

  return (
    <Container>
      <Header />
      <Title>{t("common:txt_under_maintenance")}</Title>
      <Subtitle>
        {t("common:txt_scheduled_maintenance_1")}
        <br />
        {t("common:txt_scheduled_maintenance_2")}
      </Subtitle>
      <Background>
        <Icon />
      </Background>
      <Footer />
    </Container>
  )
}

export async function getStaticProps() {
  return await propsWithTranslation({})
}

ServerError.Layout = function Layout(
  props: AppProviderProps,
) {
  const { children, theme } = props

  return (
    <ThemeProvider attribute="class" forcedTheme={theme}>
      <GlobalStyle />

      <QueryClientProvider client={queryClient}>
        <ClientSideAuthProvider>
          <NotificationProvider timeout={3000}>
            <GoogleReCaptchaProvider
              scriptProps={{ async: true }}
              reCaptchaKey={
                process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!
              }>
              <CoinsProvider>
                <AdjustProvider>
                  <Favicon />
                  <Cookies />
                  <EditorParamsProviders>
                    {children}
                  </EditorParamsProviders>
                  <UsePageViewEvent />
                  <GoogleOneTap />
                </AdjustProvider>
              </CoinsProvider>
            </GoogleReCaptchaProvider>
          </NotificationProvider>
        </ClientSideAuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}

export default ServerError
